/**
 * ActionCorrelationService
 *
 * Correlates attack_started events with their corresponding damage_dealt events
 * to synchronize damage number display with attack animation impact frames.
 *
 * Part of the Action-Based Combat Visualization system.
 */

import type { AttackStartedEventData, DamageDealtEventData } from '@drawn-of-war/shared/types/combat';

/**
 * Configuration for action correlation timing
 */
export interface ActionCorrelationConfig {
  /** Maximum time to hold pending items before fallback display (ms) */
  maxQueueAge: number;
  /** When melee animation reaches impact frame (ms from start) */
  meleeImpactOffset: number;
  /** When ranged/ability animation reaches impact (ms from start) */
  rangedImpactOffset: number;
  /** Fallback delay if correlation fails (ms) */
  fallbackDisplayDelay: number;
  /** Minimum interval between displaying damages on same target (ms) */
  minDamageInterval: number;
  /** Feature toggle - when false, returns damages immediately */
  enabled: boolean;
}

/**
 * Default configuration
 */
export const DEFAULT_CORRELATION_CONFIG: ActionCorrelationConfig = {
  maxQueueAge: 2000,           // 2 seconds max hold
  meleeImpactOffset: 300,      // 300ms into melee animation
  rangedImpactOffset: 0,       // Use projectile arrival, not fixed offset
  fallbackDisplayDelay: 100,   // 100ms fallback
  minDamageInterval: 50,       // 50ms between damages on same target
  enabled: false               // Start with feature OFF for safe rollout
};

/**
 * Pending action waiting for its damage event
 */
export interface PendingAction {
  /** Unique correlation key */
  correlationKey: string;
  /** Attacker unit ID */
  attackerId: string;
  /** Target unit ID */
  targetId: string;
  /** Server tick when damage should apply */
  estimatedImpactTick: number;
  /** Type of attack */
  attackType: 'melee' | 'ranged' | 'ability';
  /** Ability ID if applicable */
  abilityId?: string;
  /** Server tick when attack started */
  startedAtTick: number;
  /** Client time when animation started (performance.now()) */
  animationStartTime: number;
  /** Expected time until impact (ms) */
  impactFrameOffset: number;
  /** Whether animation has reached impact frame */
  animationImpactReached: boolean;
  /** Whether projectile has arrived (for ranged) */
  projectileArrived: boolean;
}

/**
 * Pending damage waiting for release conditions
 */
export interface PendingDamage {
  /** Correlation key linking to PendingAction */
  correlationKey: string;
  /** Victim unit ID */
  victimId: string;
  /** Attacker unit ID */
  attackerId: string;
  /** Damage amount */
  damageAmount: number;
  /** Damage type */
  damageType: 'physical' | 'magical';
  /** Whether critical hit */
  isCritical: boolean;
  /** Attack method */
  attackType: 'melee' | 'ranged' | 'ability';
  /** Is execute damage */
  isExecute?: boolean;
  /** Shield absorbed amount */
  shieldAbsorbed?: number;
  /** Server tick when received */
  receivedAtTick: number;
  /** Client time when received */
  receivedAtTime: number;
  /** Scheduled release time (for fallback) */
  scheduledReleaseTime: number;
}

/**
 * Damage ready for display
 */
export interface ReadyDamage {
  victimId: string;
  attackerId: string;
  damageAmount: number;
  damageType: 'physical' | 'magical';
  isCritical: boolean;
  attackType: 'melee' | 'ranged' | 'ability';
  isExecute?: boolean;
  shieldAbsorbed?: number;
}

/**
 * Statistics for debugging/monitoring
 */
export interface CorrelationStats {
  totalActionsRegistered: number;
  totalDamagesRegistered: number;
  successfulCorrelations: number;
  orphanedDamages: number;
  timeoutReleases: number;
  impactReleases: number;
  projectileReleases: number;
}

/**
 * ActionCorrelationService
 *
 * Manages the correlation between attack events and damage events
 * to ensure damage numbers appear synchronized with their visual causes.
 */
export class ActionCorrelationService {
  private config: ActionCorrelationConfig;
  private pendingActions: Map<string, PendingAction> = new Map();
  private pendingDamages: Map<string, PendingDamage[]> = new Map();
  private stats: CorrelationStats;

  constructor(config: Partial<ActionCorrelationConfig> = {}) {
    this.config = { ...DEFAULT_CORRELATION_CONFIG, ...config };
    this.stats = this.createEmptyStats();
  }

  /**
   * Update configuration
   */
  public configure(config: Partial<ActionCorrelationConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Enable or disable the correlation system
   */
  public setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Check if correlation is enabled
   */
  public isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Generate correlation key from attacker ID and impact tick
   */
  public generateCorrelationKey(attackerId: string, estimatedImpactTick: number): string {
    return `${attackerId}_${estimatedImpactTick}`;
  }

  /**
   * Register an attack_started event
   * Returns the correlation key for tracking
   * Phase 2: Only melee attacks are registered for correlation
   */
  public registerAttackStarted(
    eventData: AttackStartedEventData,
    currentTick: number
  ): string | null {
    // Phase 2: Only register melee attacks for correlation
    // Ranged/ability attacks will be added in Phases 3-4
    if (eventData.attackType !== 'melee') {
      return null;
    }

    const correlationKey = this.generateCorrelationKey(
      eventData.attackerId,
      eventData.estimatedImpactTick
    );

    // Calculate impact offset based on attack type
    const impactFrameOffset = eventData.attackType === 'melee'
      ? this.config.meleeImpactOffset
      : this.config.rangedImpactOffset;

    const pendingAction: PendingAction = {
      correlationKey,
      attackerId: eventData.attackerId,
      targetId: eventData.targetId,
      estimatedImpactTick: eventData.estimatedImpactTick,
      attackType: eventData.attackType,
      abilityId: eventData.abilityId,
      startedAtTick: currentTick,
      animationStartTime: performance.now(),
      impactFrameOffset,
      animationImpactReached: false,
      projectileArrived: false
    };

    this.pendingActions.set(correlationKey, pendingAction);
    this.stats.totalActionsRegistered++;

    return correlationKey;
  }

  /**
   * Register a damage_dealt event
   * If correlation is disabled, returns the damage immediately as ready
   * Phase 2: Only melee attacks are correlated; ranged/ability bypass for now
   */
  public registerDamageDealt(
    eventData: DamageDealtEventData,
    currentTick: number
  ): ReadyDamage | null {
    this.stats.totalDamagesRegistered++;

    // If disabled, return damage immediately
    if (!this.config.enabled) {
      return this.damageEventToReady(eventData);
    }

    // Phase 2: Only correlate melee attacks
    // Ranged attacks need projectile arrival callback (Phase 3)
    // Ability attacks need additional tuning (Phase 4)
    if (eventData.attackType !== 'melee') {
      return this.damageEventToReady(eventData);
    }

    // Try to find matching action by searching for correlation key
    // We need to find an action where attackerId matches and we're near the expected impact tick
    const matchingKey = this.findMatchingActionKey(eventData.attackerId, currentTick);

    const now = performance.now();

    const pendingDamage: PendingDamage = {
      correlationKey: matchingKey || `orphan_${eventData.attackerId}_${currentTick}`,
      victimId: eventData.victimId,
      attackerId: eventData.attackerId,
      damageAmount: eventData.damageAmount,
      damageType: eventData.damageType,
      isCritical: eventData.isCritical,
      attackType: eventData.attackType,
      isExecute: eventData.isExecute,
      shieldAbsorbed: eventData.shieldAbsorbed,
      receivedAtTick: currentTick,
      receivedAtTime: now,
      scheduledReleaseTime: now + (matchingKey ? this.config.maxQueueAge : this.config.fallbackDisplayDelay)
    };

    if (matchingKey) {
      this.stats.successfulCorrelations++;

      // Add to pending damages for this correlation
      const existing = this.pendingDamages.get(matchingKey) || [];
      existing.push(pendingDamage);
      this.pendingDamages.set(matchingKey, existing);
    } else {
      // Orphaned damage - no matching action found
      this.stats.orphanedDamages++;

      // Store with short fallback timeout
      const orphanKey = pendingDamage.correlationKey;
      const existing = this.pendingDamages.get(orphanKey) || [];
      existing.push(pendingDamage);
      this.pendingDamages.set(orphanKey, existing);
    }

    return null; // Damage is queued, not ready yet
  }

  /**
   * Called when an animation reaches its impact frame
   */
  public onAnimationImpact(attackerId: string, estimatedImpactTick: number): void {
    const correlationKey = this.generateCorrelationKey(attackerId, estimatedImpactTick);
    const action = this.pendingActions.get(correlationKey);

    if (action) {
      action.animationImpactReached = true;
    }
  }

  /**
   * Called when a projectile arrives at its target
   */
  public onProjectileArrived(attackerId: string, targetId: string, estimatedImpactTick: number): void {
    const correlationKey = this.generateCorrelationKey(attackerId, estimatedImpactTick);
    const action = this.pendingActions.get(correlationKey);

    if (action && action.targetId === targetId) {
      action.projectileArrived = true;
      this.stats.projectileReleases++;
    }
  }

  /**
   * Update loop - check for damages ready to display
   * Call this every frame from the render loop
   */
  public update(deltaTime: number): ReadyDamage[] {
    if (!this.config.enabled) {
      return [];
    }

    const readyDamages: ReadyDamage[] = [];
    const now = performance.now();
    const keysToRemove: string[] = [];

    // Check each pending action
    for (const [correlationKey, action] of this.pendingActions) {
      const isReady = this.isActionReadyForRelease(action, now);

      if (isReady) {
        // Get and release associated damages
        const damages = this.pendingDamages.get(correlationKey) || [];
        for (const damage of damages) {
          readyDamages.push(this.pendingDamageToReady(damage));
        }

        // Track release type for stats
        if (action.projectileArrived) {
          // Already tracked in onProjectileArrived
        } else if (action.animationImpactReached) {
          this.stats.impactReleases++;
        }

        keysToRemove.push(correlationKey);
      }
    }

    // Check orphaned damages (no matching action) for timeout
    for (const [correlationKey, damages] of this.pendingDamages) {
      if (!this.pendingActions.has(correlationKey)) {
        // This is orphaned - check timeout
        const readyToRelease = damages.filter(d => now >= d.scheduledReleaseTime);

        for (const damage of readyToRelease) {
          readyDamages.push(this.pendingDamageToReady(damage));
          this.stats.timeoutReleases++;
        }

        // Remove released damages
        const remaining = damages.filter(d => now < d.scheduledReleaseTime);
        if (remaining.length === 0) {
          keysToRemove.push(correlationKey);
        } else {
          this.pendingDamages.set(correlationKey, remaining);
        }
      }
    }

    // Clean up released actions and damages
    for (const key of keysToRemove) {
      this.pendingActions.delete(key);
      this.pendingDamages.delete(key);
    }

    // Clean up stale entries (over maxQueueAge)
    this.cleanupStaleEntries(now);

    return readyDamages;
  }

  /**
   * Handle unit death - release all pending damage for this unit immediately
   */
  public handleUnitDeath(unitId: string): ReadyDamage[] {
    const releasedDamages: ReadyDamage[] = [];
    const keysToRemove: string[] = [];

    // Find all damages targeting or from this unit
    for (const [correlationKey, damages] of this.pendingDamages) {
      const matchingDamages = damages.filter(
        d => d.victimId === unitId || d.attackerId === unitId
      );

      for (const damage of matchingDamages) {
        releasedDamages.push(this.pendingDamageToReady(damage));
      }

      const remaining = damages.filter(
        d => d.victimId !== unitId && d.attackerId !== unitId
      );

      if (remaining.length === 0) {
        keysToRemove.push(correlationKey);
      } else {
        this.pendingDamages.set(correlationKey, remaining);
      }
    }

    // Clean up actions involving this unit
    for (const [correlationKey, action] of this.pendingActions) {
      if (action.attackerId === unitId || action.targetId === unitId) {
        keysToRemove.push(correlationKey);
      }
    }

    for (const key of keysToRemove) {
      this.pendingActions.delete(key);
      // Only delete damages if we haven't already processed them
      if (!this.pendingDamages.has(key)) {
        continue;
      }
      this.pendingDamages.delete(key);
    }

    return releasedDamages;
  }

  /**
   * Clear all pending actions and damages
   */
  public clear(): void {
    this.pendingActions.clear();
    this.pendingDamages.clear();
  }

  /**
   * Reset statistics
   */
  public resetStats(): void {
    this.stats = this.createEmptyStats();
  }

  /**
   * Get current statistics
   */
  public getStats(): CorrelationStats {
    return { ...this.stats };
  }

  /**
   * Get pending action count (for debugging)
   */
  public getPendingActionCount(): number {
    return this.pendingActions.size;
  }

  /**
   * Get pending damage count (for debugging)
   */
  public getPendingDamageCount(): number {
    let count = 0;
    for (const damages of this.pendingDamages.values()) {
      count += damages.length;
    }
    return count;
  }

  // Private helpers

  private findMatchingActionKey(attackerId: string, currentTick: number): string | null {
    // Look for a pending action from this attacker
    // The damage should arrive at or near the estimatedImpactTick
    for (const [key, action] of this.pendingActions) {
      if (action.attackerId === attackerId) {
        // Allow some tolerance (±5 ticks) for tick matching
        const tickDiff = Math.abs(action.estimatedImpactTick - currentTick);
        if (tickDiff <= 5) {
          return key;
        }
      }
    }
    return null;
  }

  private isActionReadyForRelease(action: PendingAction, now: number): boolean {
    // For ranged attacks, wait for projectile arrival
    if (action.attackType === 'ranged') {
      return action.projectileArrived;
    }

    // For melee/ability, check if animation has reached impact frame
    if (action.animationImpactReached) {
      return true;
    }

    // Fallback: check if enough time has passed based on impactFrameOffset
    const elapsed = now - action.animationStartTime;
    if (action.impactFrameOffset > 0 && elapsed >= action.impactFrameOffset) {
      return true;
    }

    return false;
  }

  private cleanupStaleEntries(now: number): void {
    const staleKeys: string[] = [];

    for (const [key, action] of this.pendingActions) {
      const age = now - action.animationStartTime;
      if (age > this.config.maxQueueAge) {
        staleKeys.push(key);
      }
    }

    for (const key of staleKeys) {
      // Release any remaining damages before cleanup
      const damages = this.pendingDamages.get(key);
      if (damages && damages.length > 0) {
        this.stats.timeoutReleases += damages.length;
      }
      this.pendingActions.delete(key);
      this.pendingDamages.delete(key);
    }
  }

  private damageEventToReady(eventData: DamageDealtEventData): ReadyDamage {
    return {
      victimId: eventData.victimId,
      attackerId: eventData.attackerId,
      damageAmount: eventData.damageAmount,
      damageType: eventData.damageType,
      isCritical: eventData.isCritical,
      attackType: eventData.attackType,
      isExecute: eventData.isExecute,
      shieldAbsorbed: eventData.shieldAbsorbed
    };
  }

  private pendingDamageToReady(damage: PendingDamage): ReadyDamage {
    return {
      victimId: damage.victimId,
      attackerId: damage.attackerId,
      damageAmount: damage.damageAmount,
      damageType: damage.damageType,
      isCritical: damage.isCritical,
      attackType: damage.attackType,
      isExecute: damage.isExecute,
      shieldAbsorbed: damage.shieldAbsorbed
    };
  }

  private createEmptyStats(): CorrelationStats {
    return {
      totalActionsRegistered: 0,
      totalDamagesRegistered: 0,
      successfulCorrelations: 0,
      orphanedDamages: 0,
      timeoutReleases: 0,
      impactReleases: 0,
      projectileReleases: 0
    };
  }
}
